import { Injectable, OnDestroy } from '@angular/core';

export interface RecordedErrors {
  consoleErrors: string[];
  networkErrors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class BugRecordingService implements OnDestroy {
  private isRecording = false;
  private consoleErrors: string[] = [];
  private networkErrors: string[] = [];

  // Store originals so we can restore
  private originalConsoleError: (...args: any[]) => void = console.error;
  private originalConsoleWarn: (...args: any[]) => void = console.warn;
  private originalFetch: typeof window.fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;

  get recording(): boolean {
    return this.isRecording;
  }

  /**
   * Start recording console errors and network failures.
   * Clears any previous recording data.
   */
  startRecording(): void {
    if (this.isRecording) return;
    this.isRecording = true;
    this.consoleErrors = [];
    this.networkErrors = [];

    this.interceptConsole();
    this.interceptNetwork();
  }

  /**
   * Stop recording and return the collected errors.
   */
  stopRecording(): RecordedErrors {
    this.isRecording = false;
    this.restoreConsole();
    this.restoreNetwork();

    return {
      consoleErrors: [...this.consoleErrors],
      networkErrors: [...this.networkErrors]
    };
  }

  /**
   * Get current recorded errors without stopping.
   */
  getCurrentErrors(): RecordedErrors {
    return {
      consoleErrors: [...this.consoleErrors],
      networkErrors: [...this.networkErrors]
    };
  }

  ngOnDestroy(): void {
    if (this.isRecording) {
      this.stopRecording();
    }
  }

  // --- Console interception ---

  private interceptConsole(): void {
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;

    const self = this;
    const maxEntries = 50;

    console.error = function (...args: any[]) {
      if (self.consoleErrors.length < maxEntries) {
        const message = args.map(a => {
          if (a instanceof Error) return `${a.name}: ${a.message}`;
          if (typeof a === 'object') {
            try { return JSON.stringify(a); } catch { return String(a); }
          }
          return String(a);
        }).join(' ');
        self.consoleErrors.push(`[ERROR] ${message}`);
      }
      self.originalConsoleError.apply(console, args);
    };

    console.warn = function (...args: any[]) {
      if (self.consoleErrors.length < maxEntries) {
        const message = args.map(a => String(a)).join(' ');
        self.consoleErrors.push(`[WARN] ${message}`);
      }
      self.originalConsoleWarn.apply(console, args);
    };
  }

  private restoreConsole(): void {
    console.error = this.originalConsoleError;
    console.warn = this.originalConsoleWarn;
  }

  // --- Network interception ---

  private interceptNetwork(): void {
    const self = this;
    const maxEntries = 30;

    // Intercept fetch
    this.originalFetch = window.fetch;
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      try {
        const response = await self.originalFetch!.call(window, input, init);
        if (!response.ok && self.networkErrors.length < maxEntries) {
          const url = typeof input === 'string' ? input : (input as Request).url || String(input);
          self.networkErrors.push(`[${response.status}] ${init?.method || 'GET'} ${url}`);
        }
        return response;
      } catch (error: any) {
        if (self.networkErrors.length < maxEntries) {
          const url = typeof input === 'string' ? input : (input as Request).url || String(input);
          self.networkErrors.push(`[NETWORK] ${init?.method || 'GET'} ${url} - ${error?.message || 'Failed'}`);
        }
        throw error;
      }
    } as typeof window.fetch;

    // Intercept XMLHttpRequest
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...args: any[]) {
      (this as any).__bugReportMethod = method;
      (this as any).__bugReportUrl = String(url);
      return self.originalXHROpen!.apply(this, [method, url, ...args] as any);
    };

    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      this.addEventListener('loadend', function () {
        if (this.status >= 400 && self.networkErrors.length < maxEntries) {
          const method = (this as any).__bugReportMethod || 'UNKNOWN';
          const url = (this as any).__bugReportUrl || 'UNKNOWN';
          self.networkErrors.push(`[${this.status}] ${method} ${url}`);
        }
      });
      return origSend.call(this, body);
    };
  }

  private restoreNetwork(): void {
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
      this.originalXHROpen = null;
    }
  }
}
